import { getCurrentScope, onScopeDispose, ref, type Ref } from 'vue'
import { clamp } from '../math'

type AnimateReturn = {
  start: () => void
  stop: () => void
}

type AnimateOptions = {
  duration?: number
  infinite?: boolean;
  stopWhen?: () => boolean;
  loop?: boolean
  immediate?: boolean
  beforeStop?: () => void
  beforeStart?: () => void
}

export const animate = (
  callback: (time: number, delta: number, elapsedTime: number) => void,
  options?: AnimateOptions
): AnimateReturn => {
  const duration = options?.duration || 1.0
  let startTime = performance.now()
  let deltaStart: number = performance.now()
  const durationInMilliseconds = duration * 1000
  let requestId: number | null = null
  let direction: number = 1.0
  let running: boolean = false;

  const loop = (currentTime: number) => {
    if (!running) return;
    const elapsedTime = currentTime - startTime
    const now = performance.now()
    const delta = (now - deltaStart) / 1000.0
    const time = clamp(elapsedTime / durationInMilliseconds, 0, 1)
    deltaStart = now

    callback(clamp(direction < 0 && time <= 1.0 ? 1.0 - time : time, 0, 1), delta, elapsedTime);

    if (!options?.infinite && !options?.stopWhen) {
      if (elapsedTime >= durationInMilliseconds) {
        if (options?.loop) {
          direction *= -1
          startTime = currentTime
        } else {
          callback(1.0, delta, elapsedTime)
          stop()
          return
        }
      }
    }

    if (options?.stopWhen) {
      if (options.stopWhen()) {
        stop();
        return;
      }
    }

    requestId = requestAnimationFrame(loop)
  }

  const stop = () => {
    if (requestId !== null) {
      if (options?.beforeStop) {
        options.beforeStop()
      }
      cancelAnimationFrame(requestId)
      requestId = null
    }
    running = false;
  }

  const start = () => {
    running = true;

    if (requestId !== null) return
    if (options?.beforeStart) {
      options.beforeStart()
    }
    requestId = requestAnimationFrame(loop)
  }

  if (options?.immediate) {
    start()
  }

  return { start, stop }
}

export type AnimateState = {
  time: number
}

export type UseAnimateReturn = AnimateReturn & {
  time: Ref<number>
  running: Ref<boolean>
}
export type UseAnimateOptions = AnimateOptions & {
  callback?: (time: number, delta: number) => void;
  autoDestroy?: boolean;
}

export const useAnimate = (options?: UseAnimateOptions): UseAnimateReturn => {
  const timeRef = ref<number>(0)
  const running = ref<boolean>(false)

  const { start, stop } = animate(
    (time, delta) => {
      timeRef.value = time
      if (options?.callback) {
        options?.callback(time, delta)
      }
    },
    {
      ...(options || {}),
      beforeStart: () => {
        running.value = true
      },
      beforeStop: () => {
        running.value = false
      }
    }
  )

  if (getCurrentScope()) {
    onScopeDispose(() => {
      stop();
    })
  }

  return {
    start,
    stop,
    time: timeRef,
    running
  }
}
